import React, { useEffect, useState } from 'react';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import financialService from '../../api/services/financial.service';

interface AddIncomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormData {
  amount: string;
  paidTo: string;
  description: string;
  periodFromDate: string;
  periodFromTime: string;
  periodToDate: string;
  periodToTime: string;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function nowTime(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const AddIncomeModal: React.FC<AddIncomeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>({
    amount: '',
    paidTo: '',
    description: '',
    periodFromDate: todayIso(),
    periodFromTime: '00:00',
    periodToDate: todayIso(),
    periodToTime: nowTime(),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormData({
        amount: '',
        paidTo: '',
        description: '',
        periodFromDate: todayIso(),
        periodFromTime: '00:00',
        periodToDate: todayIso(),
        periodToTime: nowTime(),
      });
      setError('');
    }
  }, [isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (!formData.paidTo.trim()) {
      setError('Please enter the income source');
      return;
    }
    if (!formData.description.trim()) {
      setError('Please enter a description');
      return;
    }
    if (!formData.periodFromDate || !formData.periodToDate) {
      setError('Please specify the period from and to');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await financialService.create({
        type: 'income',
        amount,
        paidTo: formData.paidTo.trim(),
        description: formData.description.trim(),
        periodFromDate: formData.periodFromDate,
        periodFromTime: formData.periodFromTime || '00:00',
        periodToDate: formData.periodToDate,
        periodToTime: formData.periodToTime || '23:59',
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || 'Failed to add income'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Income" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Amount (ETB) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleInputChange}
            required
            min="1"
            step="1"
            placeholder="Enter amount"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Source <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="paidTo"
            value={formData.paidTo}
            onChange={handleInputChange}
            required
            placeholder="Enter income source"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            rows={2}
            placeholder="Enter description"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm resize-none"
          />
        </div>

        {/* ⭐ Period */}
        <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            Period covered by this income
          </p>

          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-1">
                From <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  name="periodFromDate"
                  value={formData.periodFromDate}
                  onChange={handleInputChange}
                  required
                  className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
                <input
                  type="time"
                  name="periodFromTime"
                  value={formData.periodFromTime}
                  onChange={handleInputChange}
                  required
                  className="w-32 text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-1">
                To <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  name="periodToDate"
                  value={formData.periodToDate}
                  onChange={handleInputChange}
                  required
                  className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
                <input
                  type="time"
                  name="periodToTime"
                  value={formData.periodToTime}
                  onChange={handleInputChange}
                  required
                  className="w-32 text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-100">
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add Income'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddIncomeModal;